import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AclAssetPermission } from '@y42/acl/models';
import { createAclPermissionDetail } from '@y42/acl/util/create-acl-permission-detail';
import { DataNoRowOverlayComponent } from '@y42/ag-grid';
import { connectState } from '@y42/angular/util';
import { ListViewGridMessages, provideSimpleListView } from '@y42/core/list-view';
import { DataTest, DataTestType, SchemaTestForm } from '@y42/data-test';
import { ErrorHandlerService } from '@y42/error-handling';
import { FeatureAggregationService, Y42FeatureToAclAssetResourceType } from '@y42/feature';
import { AssetAclService } from '@y42/feature/acl';
import { GitService } from '@y42/git';
import { isNullish } from '@y42/util/nullish';
import {
  CellClickedEvent,
  ColDef,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  ValueGetterParams,
} from 'ag-grid-community';
import { GetContextMenuItems, MenuItemDef } from 'ag-grid-community/dist/lib/entities/gridOptions';
import { filter, map, of, pairwise, switchMap, tap } from 'rxjs';
import {
  DataTestColumnsCellRendererComponent,
  DataTestColumnsCellRendererInputValue,
} from '../cell-renderers/data-test-columns-cell-renderer/data-test-columns-cell-renderer.component';
import {
  DataTestConditionCellRendererComponent,
  DataTestConditionCellRendererInputParam,
} from '../cell-renderers/data-test-condition-cell-renderer/data-test-condition-cell-renderer.component';
import {
  DataTestLatestJobStatus,
  DataTestLatestJobStatusCellRendererComponent,
} from '../cell-renderers/data-test-latest-job-status-cell-renderer/data-test-latest-job-status-cell-renderer.component';
import { DataTestTypeCellRendererComponent } from '../cell-renderers/data-test-type-cell-renderer/data-test-type-cell-renderer.component';
import { DataTestDialogStateService } from '../data-test-dialog/data-test-dialog-state.service';
import { DataTestSetupListService, DataTestSetupListUIModel } from './data-test-setup-list.service';

@UntilDestroy()
@Component({
  selector: 'y42-data-test-setup-list',
  templateUrl: './data-test-setup-list.component.html',
  styleUrls: ['./data-test-setup-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [provideSimpleListView(DataTestSetupListService)],
})
export class DataTestSetupListComponent implements OnInit {
  constructor(
    private readonly dataTestSetupListService: DataTestSetupListService,
    private readonly dataTestDialogStateService: DataTestDialogStateService,
    private readonly gitService: GitService,
    private readonly assetAclService: AssetAclService,
    private readonly featureAggregationService: FeatureAggregationService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private gridApi: GridApi<DataTest[]> | null = null;

  readonly gridOptions: GridOptions = {
    noRowsOverlayComponent: 'dataNoRowOverlayComponent',
    rowSelection: 'multiple',
    suppressCellFocus: true,
    suppressRowClickSelection: true,
    defaultColDef: {
      sortable: false,
      resizable: true,
      suppressMovable: true,
      suppressMenu: true,
    },
    animateRows: true,
    domLayout: 'autoHeight',
  };

  readonly columnDefs: ColDef[] = [
    {
      headerName: 'Latest Job Status',
      field: 'status',
      cellRenderer: 'dataTestLatestJobStatusCellRenderer',
      valueGetter: (params: ValueGetterParams<DataTestSetupListUIModel>): DataTestLatestJobStatus => ({
        value: params.data?.latestJob?.status ?? null,
        errors: params.data?.latestJob?.error_messages ?? [],
      }),
      width: 50,
    },
    {
      headerName: 'Test',
      field: 'type',
      cellRenderer: 'dataTestTypeCellRenderer',
      width: 100,
    },
    {
      headerName: 'Conditions',
      cellRenderer: 'dataTestConditionCellRenderer',
      width: 100,
      valueGetter: (params) => this.getSchemaTestDependentCellRendererParams(params),
    },
    {
      headerName: 'Columns',
      cellRenderer: 'dataTestColumnsCellRenderer',
      valueGetter: (params) => this.getSchemaTestDependentCellRendererParams(params),
    },
  ];

  readonly messages: ListViewGridMessages = {
    noDataHeader: 'There are no Data Tests',
    noDataBody: 'Please add your first data test for this table',
    addButtonLabel: 'Add Data Test',
    noDataImgSrc: 'assets/images/not-available/no-test.svg',
    addPermissionAllowedButtonTooltip: 'You do not have permission to create data tests',
  };

  readonly frameworkComponents: GridOptions['components'] = {
    dataNoRowOverlayComponent: DataNoRowOverlayComponent,
    dataTestTypeCellRenderer: DataTestTypeCellRendererComponent,
    dataTestConditionCellRenderer: DataTestConditionCellRendererComponent,
    dataTestColumnsCellRenderer: DataTestColumnsCellRendererComponent,
    dataTestLatestJobStatusCellRenderer: DataTestLatestJobStatusCellRendererComponent,
  };

  private readonly addPermissionAllowed$ = this.dataTestDialogStateService.path$.pipe(
    switchMap((tablePath) => {
      const featureType = this.featureAggregationService.getByGitPath(tablePath)?.featureType;

      if (!featureType) {
        return of(createAclPermissionDetail({ allowed: false }));
      }

      const resourceType = Y42FeatureToAclAssetResourceType[featureType];

      if (!resourceType) {
        return of(createAclPermissionDetail({ allowed: false }));
      }

      const assetPath = tablePath.split('/').slice(0, -1).join('/');

      return this.assetAclService.checkAssetPermission({
        assetPath,
        permission: AclAssetPermission.Write,
        resourceType,
      });
    }),
  );

  readonly state = connectState(this, {
    // value to pass to cell renderers
    schemaTest: this.dataTestSetupListService.dataTests$.pipe(
      map((dataTests) => dataTests.find((test): test is SchemaTestForm => test.type === DataTestType.Schema)),
    ),
    filePath: this.dataTestDialogStateService.path$,
    addPermissionAllowed: this.addPermissionAllowed$,
  });

  readonly getContextMenuItems: GetContextMenuItems = (params: GetContextMenuItemsParams<DataTest>): MenuItemDef[] => {
    const rowIndex = params.node?.rowIndex ?? null;
    if (isNullish(rowIndex)) {
      return [];
    }

    return [
      {
        name: 'Delete',
        action: async () => {
          await this.dataTestSetupListService.deleteDataTest(rowIndex);
        },
      },
    ];
  };

  ngOnInit(): void {
    this.gridOptions.getContextMenuItems = this.getContextMenuItems;

    this.resizeColumnsOnDataChanged();
  }

  gridReady(options: GridOptions<DataTest[]>) {
    this.gridApi = options.api ?? null;
    options.api?.sizeColumnsToFit();
  }

  async onCellClick($event: CellClickedEvent<DataTest>) {
    const rowIndex = $event.rowIndex;
    if (isNullish(rowIndex)) {
      return;
    }
    await this.dataTestSetupListService.cellClick(rowIndex);
  }

  async commitClick(): Promise<void> {
    try {
      await this.gitService.commit('Data Test changes', [`${this.dataTestDialogStateService.getPath()}/tests.json`]);
    } catch (err) {
      this.errorHandlerService.error('[DataTestSetupListComponent]: Could not commit', {
        err,
        toast: 'Could not commit',
      });
    }
  }

  private getSchemaTestDependentCellRendererParams(
    params: ValueGetterParams<DataTest>,
  ): DataTestConditionCellRendererInputParam | DataTestColumnsCellRendererInputValue {
    const result: DataTestConditionCellRendererInputParam | DataTestColumnsCellRendererInputValue = {
      dataTest: params.data ?? null,
      schemaTest: this.state.schemaTest ?? null,
    };
    return result;
  }

  private resizeColumnsOnDataChanged(): void {
    // hack: when a row appears in the list, fit column width
    this.dataTestSetupListService.data$
      .pipe(
        map((data) => data.length),
        pairwise(),
        filter(([a, b]) => a === 0 && b > 0),
        tap(() => this.gridApi?.sizeColumnsToFit()),
        untilDestroyed(this),
      )
      .subscribe();
  }
}
